import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Missing or invalid reset link.");
    }
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: next });
      setDone(true);
      toast.success("Password updated. You can now sign in.");
      setTimeout(() => nav("/admin/login"), 1500);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Reset failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#FDFBF7] grain relative flex items-center justify-center px-6" data-testid="reset-password-page">
      <Link to="/" className="absolute top-6 left-6 md:top-10 md:left-10 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-[#FF5722]" />
        <span className="font-serif text-xl">Photo Booth <em className="text-[#FF5722]">Memories</em></span>
      </Link>
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md border border-white/10 rounded-sm p-8 md:p-10 bg-black/40 backdrop-blur"
      >
        <div className="overline text-[#FDFBF7]/60 mb-3">Studio portal</div>
        <h1 className="font-serif text-4xl md:text-5xl leading-tight">
          Choose a new <em className="text-[#FF5722]">password</em>.
        </h1>
        <p className="text-sm text-[#FDFBF7]/60 mt-3">At least 6 characters.</p>

        {done ? (
          <div className="mt-8 rounded-sm border border-white/10 bg-white/5 p-4 text-sm text-[#FDFBF7]/85">
            Password updated. Redirecting to sign in…
          </div>
        ) : (
          <form className="mt-8 space-y-5" onSubmit={submit}>
            <div>
              <Label className="overline text-[#FDFBF7]/70">New password</Label>
              <Input
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                className="mt-2 bg-transparent border-white/20 text-[#FDFBF7] placeholder:text-white/40"
                minLength={6}
                required
                data-testid="reset-new"
              />
            </div>
            <div>
              <Label className="overline text-[#FDFBF7]/70">Confirm new password</Label>
              <Input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-2 bg-transparent border-white/20 text-[#FDFBF7] placeholder:text-white/40"
                minLength={6}
                required
                data-testid="reset-confirm"
              />
            </div>
            <button
              type="submit"
              disabled={busy || !token}
              className="w-full inline-flex items-center justify-center gap-3 px-6 py-3 rounded-full bg-[#FDFBF7] text-[#0A0A0A] hover:bg-[#FF5722] hover:text-[#FDFBF7] transition-colors disabled:opacity-60"
              data-testid="reset-submit"
            >
              {busy ? "Updating…" : "Update password"}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
